import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardSidebar from '../components/DashboardSidebar';
import { Button } from '../components/ui/button';
import CountryFlag from '../components/CountryFlag';
import {
  ArrowLeft, ArrowRight, FileText, CheckCircle, Clock, XCircle,
  Building2, Loader2
} from 'lucide-react';
import api from '../lib/api';

const AdminPartnerApplications = () => {
  const { partnerId } = useParams();
  const [partner, setPartner] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [partnersRes, appsRes] = await Promise.all([
          api.get('/admin/partners'),
          api.get(`/admin/partners/${partnerId}/applications`)
        ]);
        const p = partnersRes.data.find(x => x.user_id === partnerId);
        setPartner(p);
        setApplications(appsRes.data);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [partnerId]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'submitted': case 'under_review': case 'processing': return 'bg-[#FF5A36] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5A36]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <DashboardSidebar role="admin" />
      <main className="dashboard-content p-6 lg:p-8 pt-20 lg:pt-8" data-testid="partner-apps-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin/partners">
            <Button variant="outline" size="sm" className="border-[#D1CBBF]">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0A1E40] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {partner?.company_name || 'Partner'} — Applications
              </h1>
              <p className="text-xs text-[#4A5568]">{partner?.email} · {applications.length} applications</p>
            </div>
          </div>
        </div>

        {/* Applications */}
        {applications.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-[#D1CBBF]">
            <FileText className="w-12 h-12 text-[#D1CBBF] mx-auto mb-4" />
            <p className="text-[#4A5568]">No applications from this partner</p>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map(app => {
              const clientName = app.client_name || `${app.travelers?.[0]?.first_name || ''} ${app.travelers?.[0]?.last_name || ''}`.trim() || 'Client';
              const isPaid = app.payment_status === 'completed';
              return (
              <div key={app.application_id} className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-md transition-all" data-testid={`app-${app.application_id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-[#0A1E40] flex items-center justify-center shrink-0">
                        <span className="text-white font-bold text-sm">{clientName.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-[#0A1E40] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{clientName}</h3>
                        <div className="flex items-center gap-2 text-xs text-[#4A5568]">
                          <CountryFlag code={app.country?.code} size="xs" />
                          <span>{app.country?.name}</span>
                          <span className="text-[#D1CBBF]">·</span>
                          <span>{app.visa_type?.name}</span>
                        </div>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusColor(app.status)}`}>
                      {app.status?.replace('_', ' ')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-[#F5F0E6] rounded-xl p-3">
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">App ID</p>
                      <Link to={`/admin/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-bold hover:underline">{app.application_id}</Link>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Travelers</p>
                      <p className="text-xs font-bold text-[#0A1E40]">{app.num_travelers || 1}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Fee</p>
                      <p className="text-xs font-bold text-[#0A1E40]">₹{app.total_fee?.toLocaleString() || '0'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Payment</p>
                      <p className={`text-xs font-bold ${isPaid ? 'text-green-600' : 'text-[#FF5A36]'}`}>{isPaid ? 'Paid' : 'Pending'}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-[#4A5568] uppercase tracking-wider">Discount</p>
                      <p className="text-xs font-bold text-green-600">{app.discount_applied ? `${app.discount_applied}%` : '—'}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F5F0E6]">
                    <span className="text-xs text-[#4A5568]">Created: {new Date(app.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    <Link to={`/admin/applications/${app.application_id}`} className="text-xs text-[#FF5A36] font-semibold hover:underline flex items-center gap-1">
                      View Details <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPartnerApplications;
